import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabase';
import { DEFAULT_THEMES, applyTheme } from '../theme/themes';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  
  const [themeId, setThemeId] = useState('light-default');
  const [customColors, setCustomColors] = useState({});
  const [customRadius, setCustomRadius] = useState('');
  const [isGlassmorphism, setIsGlassmorphism] = useState(false);
  const [loadingTheme, setLoadingTheme] = useState(true);

  // Helper to load theme preferences from Supabase with localStorage cache
  const loadPreferences = async (userId) => {
    // 1. Check local cache
    const cacheKey = `theme_pref_${userId}`;
    const cached = localStorage.getItem(cacheKey);
    let prefs = null;
    if (cached) {
      try {
        prefs = JSON.parse(cached);
        applyLoadedPrefs(prefs);
      } catch (e) {
        console.error("Error parsing cached theme settings:", e);
      }
    }

    // 2. Fetch from Supabase
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('portfolio')
        .eq('id', userId)
        .maybeSingle();

      if (!error && data) {
        let dbPrefs = null;
        if (data.portfolio && data.portfolio.theme_preference) {
          dbPrefs = data.portfolio.theme_preference;
        }

        if (dbPrefs) {
          applyLoadedPrefs(dbPrefs);
          localStorage.setItem(cacheKey, JSON.stringify(dbPrefs));
        }
      }
    } catch (err) {
      console.warn("Could not load theme preferences from Supabase:", err);
    } finally {
      setLoadingTheme(false);
    }
  };

  const applyLoadedPrefs = (prefs) => {
    if (prefs.themeId) setThemeId(prefs.themeId);
    if (prefs.customColors) setCustomColors(prefs.customColors);
    if (prefs.customRadius !== undefined) setCustomRadius(prefs.customRadius);
    if (prefs.isGlassmorphism !== undefined) setIsGlassmorphism(prefs.isGlassmorphism);

    applyTheme(
      prefs.themeId || 'light-default',
      prefs.customColors || {},
      prefs.customRadius || null,
      prefs.isGlassmorphism || false
    );
  };

  // Sync theme when user logs in
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user?.id) {
      setLoadingTheme(true);
      loadPreferences(user.id);
    } else {
      // Unauthenticated default
      const guestKey = 'theme_pref_guest';
      const cached = localStorage.getItem(guestKey);
      if (cached) {
        try {
          applyLoadedPrefs(JSON.parse(cached));
        } catch {
          applyTheme('light-default', {}, null, false);
        }
      } else {
        applyTheme('light-default', {}, null, false);
      }
      setLoadingTheme(false);
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply theme in real-time on local state changes (Live Preview)
  useEffect(() => {
    applyTheme(themeId, customColors, customRadius || null, isGlassmorphism);
  }, [themeId, customColors, customRadius, isGlassmorphism]);

  // Save current settings to profiles (Supabase & localStorage)
  const saveSettings = async () => {
    if (!user?.id) {
      // Save locally for guests
      const payload = { themeId, customColors, customRadius, isGlassmorphism };
      localStorage.setItem('theme_pref_guest', JSON.stringify(payload));
      return true;
    }

    const payload = {
      themeId,
      customColors,
      customRadius,
      isGlassmorphism,
      updatedAt: new Date().toISOString()
    };

    localStorage.setItem(`theme_pref_${user.id}`, JSON.stringify(payload));

    try {
      // Update by merging with current portfolio JSON
      const { data: profile } = await supabase
        .from('profiles')
        .select('portfolio')
        .eq('id', user.id)
        .maybeSingle();

      const currentPortfolio = profile?.portfolio || {};
      const updatedPortfolio = {
        ...currentPortfolio,
        theme_preference: payload
      };

      const { error: portError } = await supabase
        .from('profiles')
        .update({ portfolio: updatedPortfolio })
        .eq('id', user.id);

      if (portError) throw portError;
      return true;
    } catch (err) {
      console.warn("Supabase save failed, relying on localStorage cache:", err);
      return false;
    }
  };

  // Revert all preferences to theme defaults
  const resetToDefault = async () => {
    const isDarkBase = themeId.includes('dark');
    const defaultId = isDarkBase ? 'dark-default' : 'light-default';
    const baseTheme = DEFAULT_THEMES.find(t => t.id === defaultId) || DEFAULT_THEMES[0];

    setThemeId(defaultId);
    setCustomColors({});
    setCustomRadius(baseTheme.radius || '10px');
    setIsGlassmorphism(baseTheme.glassmorphism || false);

    if (user?.id) {
      localStorage.removeItem(`theme_pref_${user.id}`);
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('portfolio')
          .eq('id', user.id)
          .maybeSingle();

        if (profile) {
          const currentPortfolio = profile.portfolio || {};
          delete currentPortfolio.theme_preference;
          await supabase
            .from('profiles')
            .update({ portfolio: currentPortfolio })
            .eq('id', user.id);
        }
      } catch (err) {
        console.warn("Could not reset theme preferences on Supabase:", err);
      }
    } else {
      localStorage.removeItem('theme_pref_guest');
    }
  };

  // Temporary preview method
  const livePreview = (tId, colors = {}, radius = '', isGlass = false) => {
    applyTheme(tId, colors, radius || null, isGlass);
  };

  return (
    <ThemeContext.Provider value={{
      themes: DEFAULT_THEMES,
      themeId,
      setThemeId,
      customColors,
      setCustomColors,
      customRadius,
      setCustomRadius,
      isGlassmorphism,
      setIsGlassmorphism,
      saveSettings,
      resetToDefault,
      livePreview,
      loadingTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within a ThemeProvider');
  }
  return context;
};
